import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Users,
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Building2
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const InvitationSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [invitationData, setInvitationData] = useState(null);

  const { bootstrapSession } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;
  const isPasswordValid = passwordStrength >= 3 && password.length >= 8;

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !emailParam) {
        setError('Invalid invitation link. Please check your email for the correct link.');
        setValidating(false);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/invitation/validate', {
          params: { token, email: emailParam }
        });

        if (response.data.success) {
          setTokenValid(true);
          setInvitationData(response.data);
        } else {
          setError(response.data.message || 'Invalid or expired invitation');
        }
      } catch (err) {
        const message = err.response?.data?.message || 'Failed to validate invitation';
        setError(message);
      } finally {
        setValidating(false);
        setLoading(false);
      }
    };

    validateToken();
  }, [token, emailParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!isPasswordValid) {
      setError('Please create a stronger password');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post('/auth/invitation/accept', {
        token,
        email: emailParam,
        password
      });

      if (response.data.success) {
        // Bootstrap the auth session with the user data
        // The httpOnly cookie is already set by the server
        bootstrapSession(response.data.token, response.data.user);

        // Navigate to dashboard
        navigate('/dashboard', {
          state: {
            message: `Welcome to ${invitationData?.organization?.name || 'the team'}!`
          }
        });
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to complete signup';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || validating) {
    return (
      <div id="invitation-signup-loading" className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-brand-primary mx-auto mb-4" />
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div id="invitation-signup-invalid" className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500 mb-6">
            If you believe this is an error, please contact your organization administrator to resend the invitation.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div id="invitation-signup-page" className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-1 text-3xl font-bold mb-4">
            <span className="text-brand-primary">Hello</span>
            <span className="text-brand-secondary">PC</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Join {invitationData?.organization?.name || 'the Team'}
          </h1>
          <p className="text-gray-600">
            Complete your account setup to get started
          </p>
        </div>

        {/* Signup Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Invitation Info */}
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center text-white">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {invitationData?.organization?.name}
                </p>
                <p className="text-xs text-gray-500">
                  You've been invited as <span className="font-medium capitalize">{invitationData?.user?.role}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center text-sm">
                <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={emailParam || ''}
                  disabled
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
              </div>
            </div>

            {/* Name display */}
            {invitationData?.user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={`${invitationData.user.firstName} ${invitationData.user.lastName}`}
                    disabled
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Create Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Create a secure password"
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Password strength indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          passwordStrength >= level
                            ? level <= 1
                              ? 'bg-red-500'
                              : level <= 2
                              ? 'bg-yellow-500'
                              : level <= 3
                              ? 'bg-blue-500'
                              : 'bg-green-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="space-y-1">
                    <p className={`text-xs flex items-center ${passwordChecks.length ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordChecks.length ? <CheckCircle className="w-3 h-3 mr-1" /> : <span className="w-3 h-3 mr-1" />}
                      At least 8 characters
                    </p>
                    <p className={`text-xs flex items-center ${passwordChecks.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordChecks.uppercase ? <CheckCircle className="w-3 h-3 mr-1" /> : <span className="w-3 h-3 mr-1" />}
                      One uppercase letter
                    </p>
                    <p className={`text-xs flex items-center ${passwordChecks.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordChecks.lowercase ? <CheckCircle className="w-3 h-3 mr-1" /> : <span className="w-3 h-3 mr-1" />}
                      One lowercase letter
                    </p>
                    <p className={`text-xs flex items-center ${passwordChecks.number ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordChecks.number ? <CheckCircle className="w-3 h-3 mr-1" /> : <span className="w-3 h-3 mr-1" />}
                      One number
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-md focus:ring-brand-primary focus:border-brand-primary ${
                    confirmPassword && password !== confirmPassword
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !isPasswordValid || password !== confirmPassword}
              className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Complete Setup'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationSignup;
